import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import InfoIcon from '@mui/icons-material/Info';
import { Accordion, AccordionDetails, AccordionSummary, CardMedia, MenuItem, SelectChangeEvent, Stack, Tooltip, Typography } from '@mui/material';
import { useFormik } from 'formik';
import * as React from 'react';
import { useTranslation } from 'react-i18next';
import Swal from 'sweetalert2';
import * as yup from 'yup';
import { getMinimumWaterDepth, getSlopeParameter } from '../calculation';
import { designDredgingLevel, designWaterLevel, distanceBetweenPileAxis, geometryPhysicalData, minimumWaterDepth, pileDiameter, quaySlope, typeOfQuay } from '../conversion/geometry';
import {
    depthParameterKh,
    revetmentCharacteristicsForPilarczykFormulaPhysicalData,
    slopeParameter,
    stabilityParameter,
    turbolenceParameterKt,
    widthOfTheProtectionW,
} from '../conversion/physicalData';
import DropDown from '../shared/component/DropDown';
import InputText from '../shared/component/InputText';
import { ETypeOfQuay, EUnitOfMeasure } from '../shared/enum';
import {
    IInputGeometry,
    IParametersForTheDesignOfBedProtection,
    IRevetmentCharacteristicsForPilarczykFormula,
    TInputGeometry,
    TParametersForTheDesignOfBedProtection,
    TRevetmentCharacteristicsForPilarczykFormula,
} from '../shared/iProject';
import variable from '../shared/style/variable';
import { cleanAlert } from '../store/alert/alertSlice';
import { cleanEngine } from '../store/engine/engineSlice';
import { useAppDispatch, useAppSelector } from '../store/hook';
import { cleanMap } from '../store/map/mapSlice';
import { removeMaterialDoubleLayer, selectProject, setGeometry, setPhysicalData } from '../store/project/projectSlice';
import { cleanResult } from '../store/result/resultSlice';
import DistanceBetweenPileAxis from './DistanceBetweenPileAxis.png';
import PilarczykFormula from './PilarczykFormula.png';
import SlopeParameterFormula from './SlopeParameterFormula.png';
import Material from './material/Index';
import StabilityParameterTable from './stabilityParameter_table.png';

interface IInputProps {}

interface IInput {
    quayCharacteristics: IInputGeometry;
    parametersForTheDesignOfBedProtection: IParametersForTheDesignOfBedProtection;
    revetmentCharacteristicsForPilarczykFormula: IRevetmentCharacteristicsForPilarczykFormula;
}

interface IInputChild extends IInputGeometry, IParametersForTheDesignOfBedProtection, IRevetmentCharacteristicsForPilarczykFormula {}

const Input = (props: IInputProps) => {
    const project = useAppSelector(selectProject);
    const dispatch = useAppDispatch();

    const { t } = useTranslation();

    let timer = React.useRef<NodeJS.Timeout>();

    let input: IInput = {
        quayCharacteristics: {
            typeOfQuay: project.data!.geometry!.typeOfQuay,
            quaySlope: project.data!.geometry!.quaySlope,
            pileDiameter: project.data!.geometry!.pileDiameter,
            distanceBetweenPileAxis: project.data!.geometry!.distanceBetweenPileAxis,
            minimumWaterDepth: project.data!.geometry!.minimumWaterDepth,
            designWaterLevel: project.data!.geometry!.designWaterLevel,
            designDredgingLevel: project.data!.geometry!.designDredgingLevel,
            minimumUnderKeelClerance: project.data!.geometry!.minimumUnderKeelClerance,
            distanceBetweenVesselAndQuay: project.data!.geometry!.distanceBetweenVesselAndQuay,
        },
        parametersForTheDesignOfBedProtection: {
            depthParameterKh: project.data!.material!.physicalData[0].depthParameterKh,
            slopeParameter: getSlopeParameter(project.data!.geometry!.quaySlope),
            turbolenceParameterKt: project.data!.material!.physicalData[0].turbolenceParameterKt,
            stabilityParameter: project.data!.material!.physicalData[0].stabilityParameter,
        },
        revetmentCharacteristicsForPilarczykFormula: {
            widthOfTheProtectionW: project.data!.material!.physicalData[0].widthOfTheProtectionW,
        },
    };

    const formik = useFormik<IInputChild>({
        initialValues: {
            ...input.quayCharacteristics,
            ...input.parametersForTheDesignOfBedProtection,
            ...input.revetmentCharacteristicsForPilarczykFormula,
        },
        validationSchema: yup.object({
            typeOfQuay: yup.number().required(),
            quaySlope: yup.number().required(),
            pileDiameter: yup.number().required(),
            distanceBetweenPileAxis: yup.number().required(),
            designWaterLevel: yup.number().required(),
            designDredgingLevel: yup.number().required(),
            minimumWaterDepth: yup.number().required(),
            minimumUnderKeelClerance: yup.number().required(),
            distanceBetweenVesselAndQuay: yup.number().required(),
            depthParameterKh: yup.number().required(),
            slopeParameter: yup.number().required(),
            turbolenceParameterKt: yup.number().required(),
            stabilityParameter: yup.number().required(),
            widthOfTheProtectionW: yup.number().required(),
        }),
        onSubmit: values => {},
    });

    const handleChangeGeometry = (event: React.ChangeEvent<any> | SelectChangeEvent<any>) => {
        let { name, value } = event.target;
        let valueAsNumber = parseFloat(value);
        let updateValue = { [name]: valueAsNumber };
        if (!isNaN(valueAsNumber)) {
            let minimumWaterDepth = project.data?.geometry?.minimumWaterDepth!;
            let quaySlopeValue = name == quaySlope ? valueAsNumber : project.data?.geometry?.quaySlope!;
            let pileDiameterValue = name == pileDiameter ? valueAsNumber : project.data?.geometry?.pileDiameter!;
            let distanceBetweenPileAxisValue = name == distanceBetweenPileAxis ? valueAsNumber : project.data?.geometry?.distanceBetweenPileAxis!;
            if (name == designWaterLevel) {
                minimumWaterDepth = getMinimumWaterDepth(value, formik.values.designDredgingLevel);
                formik.setValues({ ...formik.values, ...updateValue, minimumWaterDepth });
            } else if (name == designDredgingLevel) {
                minimumWaterDepth = getMinimumWaterDepth(formik.values.designWaterLevel, value);
                formik.setValues({ ...formik.values, ...updateValue, minimumWaterDepth });
            } else if (name == typeOfQuay) {
                quaySlopeValue = 90;
                if (value !== ETypeOfQuay.Vertical) {
                    quaySlopeValue = 45;
                    pileDiameterValue = project.data?.project?.unitOfMeasure == EUnitOfMeasure.Metric ? 1 : 3.2;
                    distanceBetweenPileAxisValue = project.data?.project?.unitOfMeasure == EUnitOfMeasure.Metric ? 3 : 9.8;
                    dispatch(removeMaterialDoubleLayer());
                }
                let slopeParameter = getSlopeParameter(quaySlopeValue);
                formik.setValues({
                    ...formik.values,
                    ...updateValue,
                    quaySlope: quaySlopeValue,
                    pileDiameter: pileDiameterValue,
                    distanceBetweenPileAxis: distanceBetweenPileAxisValue,
                    slopeParameter,
                });
                dispatch(setPhysicalData({ ...project.data!.material!.physicalData[0]!, slopeParameter }));
            } else if (name == quaySlope) {
                let slopeParameter = getSlopeParameter(value);
                formik.setValues({
                    ...formik.values,
                    ...updateValue,
                    slopeParameter,
                });
                dispatch(setPhysicalData({ ...project.data!.material!.physicalData[0]!, slopeParameter }));
            } else {
                formik.setValues({ ...formik.values, ...updateValue });
            }

            dispatch(
                setGeometry({
                    ...project.data!.geometry!,
                    ...updateValue,
                    minimumWaterDepth,
                    quaySlope: quaySlopeValue,
                    pileDiameter: pileDiameterValue,
                    distanceBetweenPileAxis: distanceBetweenPileAxisValue,
                    doubleLayerNearToTheQuayWall: name == typeOfQuay && value != ETypeOfQuay.Vertical ? false : project.data?.geometry?.doubleLayerNearToTheQuayWall!,
                }),
            );
            dispatch(cleanResult());
            dispatch(cleanEngine());
            dispatch(cleanMap());
            dispatch(cleanAlert());

            //Alert
            if (timer.current) {
                clearTimeout(timer.current);
                timer.current = undefined;
            }
            if (name == quaySlope) {
                if ((value as number) < 0 || (value as number) > 90) {
                    timer.current = setTimeout(() => {
                        Swal.fire({
                            text: t('quaySlopeLimit'),
                            icon: 'warning',
                        });
                    }, 2500);
                } else if ((value as number) > 34 && (value as number) < 90) {
                    timer.current = setTimeout(() => {
                        Swal.fire({
                            text: t('quaySlopeInfo'),
                            icon: 'warning',
                        });
                    }, 2500);
                }
            }
            if (name == pileDiameter) {
                let maxValueLabel = project.data?.project?.unitOfMeasure == EUnitOfMeasure.Metric ? `5 ${t('meters')}` : `16.4 ${t('feet')}`;
                if ((value as number) < 0 || (value as number) > parseFloat(maxValueLabel.split(' ')[0])) {
                    timer.current = setTimeout(() => {
                        Swal.fire({
                            text: t('pileDiameterLimit', { maxValueLabel }),
                            icon: 'warning',
                        });
                    }, 2500);
                }
            }
            if (name == distanceBetweenPileAxis) {
                let maxValueLabel = project.data?.project?.unitOfMeasure == EUnitOfMeasure.Metric ? `10 ${t('meters')}` : `32.8 ${t('feet')}`;
                if ((value as number) < 0 || (value as number) > parseFloat(maxValueLabel.split(' ')[0])) {
                    timer.current = setTimeout(() => {
                        Swal.fire({
                            text: t('distanceBetweenPileAxisLimit', { maxValueLabel }),
                            icon: 'warning',
                        });
                    }, 2500);
                }
            }
            if (name == minimumWaterDepth) {
                let maxValueLabel = project.data?.project?.unitOfMeasure == EUnitOfMeasure.Metric ? `50 ${t('meters')}` : `164 ${t('feet')}`;
                if ((value as number) < 0 || (value as number) > parseFloat(maxValueLabel.split(' ')[0])) {
                    timer.current = setTimeout(() => {
                        Swal.fire({
                            text: t('minimumWaterDepthLimit'),
                            icon: 'warning',
                        });
                    }, 2500);
                }
            }
            if (name == designDredgingLevel) {
                if ((value as number) > 0) {
                    timer.current = setTimeout(() => {
                        Swal.fire({
                            text: t('warningNegativeValue'),
                            icon: 'warning',
                        });
                    }, 2500);
                }
            }
        }
    };

    const checkAlert = (name: string, value: number) => {
        if (timer.current) {
            clearTimeout(timer.current);
            timer.current = undefined;
        }
        if (name == turbolenceParameterKt) {
            if ((value as number) < 0 || (value as number) > 3) {
                timer.current = setTimeout(() => {
                    Swal.fire({
                        text: t('turbolenceParameterKtLimit'),
                        icon: 'warning',
                    });
                }, 2500);
            }
        }
        if (name == stabilityParameter) {
            if ((value as number) < 0 || (value as number) > 2) {
                timer.current = setTimeout(() => {
                    Swal.fire({
                        text: t('stabilityParameterLimit'),
                        icon: 'warning',
                    });
                }, 2500);
            }
        }
        if (name == widthOfTheProtectionW) {
            if ((value as number) < 0) {
                timer.current = setTimeout(() => {
                    Swal.fire({
                        text: t('widthOfTheProtectionWLimit'),
                        icon: 'warning',
                    });
                }, 2500);
            }
        }
    };

    const handleChangePhysicalData = (event: any) => {
        let { name, value } = event.target;
        let valueAsNumber = parseFloat(value);
        let updateValue = { [name]: valueAsNumber };
        if (!isNaN(valueAsNumber)) {
            formik.setValues({ ...formik.values, ...updateValue });
            dispatch(setPhysicalData({ ...project.data!.material!.physicalData[0]!, ...updateValue }));
            dispatch(cleanResult());
            dispatch(cleanEngine());
            dispatch(cleanMap());
            dispatch(cleanAlert());

            checkAlert(name, value);
        }
    };

    const handleHidden = (name: string) => {
        if (name == quaySlope && formik.values[typeOfQuay] == ETypeOfQuay.Vertical) return true;
        if ((name == distanceBetweenPileAxis || name == pileDiameter) && (formik.values[typeOfQuay] == ETypeOfQuay.Vertical || formik.values[typeOfQuay] == ETypeOfQuay.Sloped)) return true;
        return false;
    };

    const handleDisabled = (name: string) => {
        if (name == minimumWaterDepth) return true;
        if (name == slopeParameter) return true;
        return false;
    };

    const handleTextTooltip = (name: string, value: any) => {
        if (name == turbolenceParameterKt) return t('infoTurbolenceParameter');
        if (name == widthOfTheProtectionW) return t('infoWidthOfTheProtectionW');
        if (name == stabilityParameter) return <CardMedia sx={{ height: 120, width: 500 }} image={StabilityParameterTable} title='Stability parameter' />;
        if (name == designWaterLevel) return t('designWaterLevelInfo');
        if (name == depthParameterKh) return t('depthParameterKhInfo');
        if (name == slopeParameter) return <CardMedia sx={{ height: 96, width: 248 }} image={SlopeParameterFormula} title='Stability parameter' />;
        if (name == distanceBetweenPileAxis) return <CardMedia sx={{ height: 185, width: 300 }} image={DistanceBetweenPileAxis} title='Distance Between Pile Axis' />;
    };

    return (
        <Stack variant='scrollable'>
            <form onSubmit={formik.handleSubmit}>
                <Accordion disableGutters>
                    <AccordionSummary expandIcon={<ExpandMoreIcon />} aria-controls='panel1a-content' id='panel1a-header'>
                        <Typography>{t('quayProperties')}</Typography>
                    </AccordionSummary>
                    <AccordionDetails>
                        {Object.keys(input.quayCharacteristics).map(x => {
                            if (x === typeOfQuay) {
                                return (
                                    <DropDown inputLabel={t(x)} labelId={`${x}-label`} id={x} name={x} value={formik.values[x as TInputGeometry]} onChange={handleChangeGeometry}>
                                        <MenuItem value={ETypeOfQuay.Vertical}>{t('vertical')}</MenuItem>
                                        <MenuItem value={ETypeOfQuay.Sloped}>{t('sloped')}</MenuItem>
                                        <MenuItem value={ETypeOfQuay.SlopedWithPiles}>{t('slopedWithPiles')}</MenuItem>
                                    </DropDown>
                                );
                            } else {
                                return (
                                    <InputText
                                        key={x}
                                        hidden={handleHidden(x)}
                                        disabled={handleDisabled(x)}
                                        error={formik.touched[x as TInputGeometry] && Boolean(formik.errors[x as TInputGeometry])}
                                        helperText={formik.touched[x as TInputGeometry] && (formik.errors[x as TInputGeometry] as React.ReactNode)}
                                        customValue={formik.values[x as TInputGeometry]}
                                        id={x}
                                        isNumber={typeof input.quayCharacteristics[x as TInputGeometry] == 'number'}
                                        inputLabel={t(x, { unitOfMeasure: geometryPhysicalData[x as TInputGeometry]?.unitOfMeasure[project.data!.project!.unitOfMeasure!] })}
                                        name={x}
                                        customOnChange={handleChangeGeometry}
                                        textTooltip={handleTextTooltip(x, formik.values[x as TParametersForTheDesignOfBedProtection])}
                                    />
                                );
                            }
                        })}
                    </AccordionDetails>
                </Accordion>
                <Accordion disableGutters>
                    <AccordionSummary expandIcon={<ExpandMoreIcon />} aria-controls='panel2a-content' id='panel2a-header'>
                        <Typography>
                            <Stack flexDirection={'row'} alignItems={'center'} gap={1}>
                                {t('parametersForBedProtectionDesign')}
                                <Tooltip title={<CardMedia sx={{ height: 96, width: 500 }} image={PilarczykFormula} title='Pilarczyk Formula' />}>
                                    <InfoIcon style={{ fill: variable.green }} fontSize='small' />
                                </Tooltip>
                            </Stack>
                        </Typography>
                    </AccordionSummary>
                    <AccordionDetails>
                        {Object.keys(input.parametersForTheDesignOfBedProtection).map(x => (
                            <InputText
                                key={x}
                                hidden={handleHidden(x)}
                                disabled={handleDisabled(x)}
                                error={formik.touched[x as TParametersForTheDesignOfBedProtection] && Boolean(formik.errors[x as TParametersForTheDesignOfBedProtection])}
                                helperText={formik.touched[x as TParametersForTheDesignOfBedProtection] && formik.errors[x as TParametersForTheDesignOfBedProtection]}
                                customValue={formik.values[x as TParametersForTheDesignOfBedProtection]}
                                id={x}
                                textTooltip={handleTextTooltip(x, formik.values[x as TParametersForTheDesignOfBedProtection])}
                                isNumber={typeof input.parametersForTheDesignOfBedProtection[x as TParametersForTheDesignOfBedProtection] == 'number'}
                                inputLabel={t(x)}
                                name={x}
                                customOnChange={handleChangePhysicalData}
                            />
                        ))}
                    </AccordionDetails>
                </Accordion>
                <Accordion disableGutters>
                    <AccordionSummary expandIcon={<ExpandMoreIcon />} aria-controls='panel3a-content' id='panel3a-header'>
                        <Typography>{t('revetmentCharacteristics')}</Typography>
                    </AccordionSummary>
                    <AccordionDetails>
                        <InputText disabled value={project.data!.material!.translations![0]?.name} inputLabel={t('material')} />
                        {Object.keys(input.revetmentCharacteristicsForPilarczykFormula).map(x => (
                            <InputText
                                key={x}
                                error={formik.touched[x as TRevetmentCharacteristicsForPilarczykFormula] && Boolean(formik.errors[x as TRevetmentCharacteristicsForPilarczykFormula])}
                                helperText={formik.touched[x as TRevetmentCharacteristicsForPilarczykFormula] && formik.errors[x as TRevetmentCharacteristicsForPilarczykFormula]}
                                customValue={input.revetmentCharacteristicsForPilarczykFormula[x as TRevetmentCharacteristicsForPilarczykFormula]}
                                id={x}
                                inputLabel={t(x, {
                                    unitOfMeasure:
                                        revetmentCharacteristicsForPilarczykFormulaPhysicalData[x as TRevetmentCharacteristicsForPilarczykFormula].unitOfMeasure[project.data!.project!.unitOfMeasure!],
                                })}
                                isNumber={typeof input.revetmentCharacteristicsForPilarczykFormula[x as TRevetmentCharacteristicsForPilarczykFormula] == 'number'}
                                textTooltip={handleTextTooltip(x, formik.values[x as TParametersForTheDesignOfBedProtection])}
                                name={x}
                                customOnChange={handleChangePhysicalData}
                            />
                        ))}
                        <Material />
                    </AccordionDetails>
                </Accordion>
            </form>
        </Stack>
    );
};

export default Input;
